import React from 'react';
import { Card, ActionIcon, Flex, Stack, Title } from '@mantine/core';
import { IconTrash } from '@tabler/icons-react';
import { SectionGroupNode } from './form-helpers';
import FormFieldBinding, { FormFieldWiring } from './FormFieldBinding';
import { FormDefinitionField } from '@/interfaces/api/models';

export interface FormSectionInstanceProps extends FormFieldWiring {
    sectionGroup: SectionGroupNode;
    formDefinitionFields: FormDefinitionField[];
    form: any;
    onRemoveInstance: (sectionGroup: SectionGroupNode) => void;
    disabled?: boolean;
    // Set only on the top-level call from FormSection when it's rendered inside ManageFormModal's
    // Accordion.Panel (mirrors `suppressHeading`'s reasoning) — that panel's Accordion.Item already
    // has its own border, so this Card's own border would otherwise double up against it. Never
    // passed down to a nested child section's own instance, whose border is a real, non-redundant
    // separator between nested repeatable instances.
    suppressBorder?: boolean;
    // The section's heading text, or null when FormSection has decided this instance shouldn't
    // carry one (see its own showHeading comment). Rendered here, in line with the delete action on
    // the same row, rather than as a separate block FormSection used to render above this Card.
    heading?: string | null;
    // Set together with suppressBorder on the accordion call site — see FormSection's doc comment.
    suppressDeleteAction?: boolean;
}

function FormSectionInstance({ sectionGroup, formDefinitionFields, form, onRemoveInstance, disabled, suppressBorder, suppressDeleteAction, heading, ...wiring }: FormSectionInstanceProps) {
    const showDelete = sectionGroup.isDeletable && !disabled && !suppressDeleteAction;
    return (
        <Card withBorder={!suppressBorder} radius="sm" mt="sm" p="sm">
            {(heading || showDelete) && (
                <Flex align="center" gap="xs" wrap="nowrap" mb={heading ? 'xs' : undefined}>
                    {heading && <Title order={4} style={{ flexGrow: 1, margin: 0 }}>{heading}</Title>}
                    {showDelete && (
                        <ActionIcon variant="subtle" color="red" aria-label="remove section instance" onClick={() => onRemoveInstance(sectionGroup)} ml="auto">
                            <IconTrash size={16} />
                        </ActionIcon>
                    )}
                </Flex>
            )}
            <Stack gap="xs">
                {sectionGroup.formItems.map((item) => {
                    const field = formDefinitionFields.find((f) => f.ID === item.FormDefinitionFieldID);
                    if (!field) return null;
                    return <FormFieldBinding key={form.key(item.ID)} item={item} field={field} form={form} disabled={disabled} {...wiring} />;
                })}
            </Stack>
        </Card>
    );
}

export default React.memo(FormSectionInstance);
